import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Typography } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useToast } from '_/hooks/use-toast'
import { useConfirm } from '@refera/ui-web'

import * as Modal from '_components/modal/generic-modal'
import { Select, DateTimePicker } from '_components/inputs'
import Radio from '_components/inputs/Radio'

import Attachments from '_components/refera-ui/Attachments'
import Button from '_components/button'
import ConfirmRemoveDialog from '_components/dialogs/ConfirmRemoveDialog'
import {
  createPropertyKeyRegistry,
  updatePropertyKeyRegistry,
  deletePropertyKeyRegistry,
} from '_/modules/service-orders/actions'
import PreviewComponent from '_components/common/preview-component'
import useStyles from './styles'
import { getTradesmanSequence } from '_modules/tradesman-sequence/actions'
import { getTradesmanSequenceResults } from '_modules/tradesman-sequence/selectors'

const KeyRegistryModal = ({
  open,
  onClose,
  editData = null,
  budgets = [],
  activeBudgetId = null,
  onRefresh,
  serviceOrder,
}) => {
  const styles = useStyles()
  const { showToast } = useToast()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [attachments, setAttachments] = useState([])
  const { isConfirmed } = useConfirm()

  const isEditMode = Boolean(editData)

  const methods = useForm({})

  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = methods

  const reservationType = watch('reservation_type')
  const hasActiveBudget = Boolean(activeBudgetId)
  const tradesmanSequenceResults = useSelector(getTradesmanSequenceResults)

  const budgetOptions = useMemo(() => {
    if (!budgets?.length) {
      return []
    }

    const sortedBudgets = budgets.sort((a, b) => {
      if (a.id === activeBudgetId) return -1
      if (b.id === activeBudgetId) return 1
      return a.id - b.id
    })

    const options = sortedBudgets.map(budget => ({
      id: budget.id,
      label: `${budget.id} - ${budget.company_name || ''}`,
      value: budget.id,
    }))
    return options
  }, [budgets, activeBudgetId])

  const tradesmanOptions = useMemo(() => {
    if (!tradesmanSequenceResults?.length) {
      return []
    }

    const lastSequence = tradesmanSequenceResults.slice().sort((a, b) => b?.id - a?.id)[0]

    if (!lastSequence?.candidates?.length) {
      return []
    }

    const candidates = lastSequence.candidates?.map(candidate => ({
      id: candidate.serviceProvider?.id,
      label: candidate.serviceProvider?.companyName,
      value: candidate.serviceProvider?.id,
    }))

    const serviceOrderTradesmanExists = candidates.some(
      candidate => candidate.id === serviceOrder?.tradesman
    )

    return candidates.sort((a, b) => {
      if (serviceOrderTradesmanExists) {
        if (a.id === serviceOrder?.tradesman && b.id !== serviceOrder?.tradesman) return -1
        if (a.id !== serviceOrder?.tradesman && b.id === serviceOrder?.tradesman) return 1
      }

      return a.label.localeCompare(b.label)
    })
  }, [tradesmanSequenceResults, serviceOrder])

  useEffect(() => {
    if (editData) {
      reset({
        date_key_action: editData?.dateAndTimeKeyAction,
        reservation_type: editData?.reservationType,
        tradesman_id: editData?.tradesman_id,
      })
      if (editData?.attachment) {
        setAttachments([
          {
            id: editData?.uuid,
            file: editData?.attachment,
          },
        ])
      }
    }
  }, [editData, reset, hasActiveBudget])

  useEffect(() => {
    if (serviceOrder?.id) dispatch(getTradesmanSequence(serviceOrder?.id))
  }, [serviceOrder, dispatch])

  const handleClose = useCallback(() => {
    if (!isLoading) {
      reset()
      setAttachments([])
      onClose()
    }
  }, [isLoading, reset, onClose])

  const handleAddAttachment = useCallback(files => {
    setAttachments(prev => [...prev, ...files])
  }, [])

  const handleRemoveAttachment = useCallback(index => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleSuccessAndCleanup = useCallback(() => {
    showToast({
      type: 'success',
      message: isEditMode
        ? 'Registro de chave atualizado com sucesso!'
        : 'Registro de chave criado com sucesso!',
    })
    if (onRefresh) onRefresh()
    handleClose()
    setIsLoading(false)
  }, [isEditMode, showToast, onRefresh, handleClose])

  const onSubmit = useCallback(
    data => {
      if (isLoading) return
      setIsLoading(true)

      let payload
      if (attachments.length > 0) {
        const formData = new FormData()

        if (data?.budget_id) formData.append('budget', data?.budget_id)
        formData.append('type', data?.type || '')
        formData.append('reservation_type', data?.reservation_type || '')
        if (data?.tradesman_id) formData.append('tradesman_id', data?.tradesman_id || '')
        formData.append(
          'date_and_time_key_action',
          data?.date_key_action ? new Date(data?.date_key_action).toISOString() : ''
        )

        if (attachments[0] instanceof File) {
          formData.append('attachment', attachments[0])
        }

        payload = formData
      } else {
        payload = {
          type: data?.type || '',
          reservation_type: data?.reservation_type || '',
          date_and_time_key_action: data?.date_key_action
            ? new Date(data?.date_key_action).toISOString()
            : '',
        }

        if (data?.budget_id) payload.budget = data?.budget_id
        if (data?.tradesman_id) payload.tradesman_id = data?.tradesman_id
      }

      if (isEditMode && editData?.id) {
        dispatch(updatePropertyKeyRegistry(serviceOrder?.id, editData?.id, payload))
          .catch(error => {
            console.error('Erro ao atualizar registro:', error)
            showToast({
              type: 'error',
              message: 'Erro ao atualizar registro de chave. Tente novamente.',
            })
          })
          .finally(handleSuccessAndCleanup)
      } else {
        dispatch(createPropertyKeyRegistry(serviceOrder?.id, payload))
          .catch(error => {
            console.error('Erro ao salvar registro:', error)
            showToast({
              type: 'error',
              message: 'Erro ao salvar registro de chave. Tente novamente.',
            })
          })
          .finally(handleSuccessAndCleanup)
      }
    },
    [
      isLoading,
      attachments,
      isEditMode,
      editData,
      dispatch,
      onRefresh,
      showToast,
      handleClose,
      serviceOrder,
    ]
  )

  const handleDelete = useCallback(() => {
    if (!isEditMode || isLoading) return
    setIsLoading(true)

    dispatch(deletePropertyKeyRegistry(serviceOrder?.id, editData?.id))
      .then(() => {
        showToast({
          type: 'success',
          message: 'Registro de chave excluído com sucesso!',
        })
        if (onRefresh) onRefresh()
        handleClose()
      })
      .catch(error => {
        console.error('Erro ao excluir registro:', error)
        showToast({
          type: 'error',
          message: 'Erro ao excluir registro de chave. Tente novamente.',
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [isEditMode, isLoading, editData, dispatch, onRefresh, showToast, handleClose, serviceOrder])

  const [previewState, setPreviewState] = useState({
    open: false,
    selectedIndex: undefined,
  })

  const handlePictureItemClick = index =>
    setPreviewState({
      open: true,
      selectedIndex: index,
    })

  if (!open) return null

  return (
    <>
      <Modal.Root open={open} onClose={handleClose} maxWidth="md" keepMounted>
        <Modal.TitleModal title="Registro de chaves" />

        <Modal.Content className={styles.modal}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Radio
                    name="reservation_type"
                    label="Tipo de reserva"
                    required
                    labelRadio1="Para orçar"
                    labelRadio2="Para executar o serviço"
                    valueRadio1="for_budget"
                    valueRadio2="for_execute_service"
                    radioGroupClasses={styles.tipoReservaGroup}
                    radioClasses={styles.tipoReservaRadio}
                    defaultValue={
                      editData?.reservationType ||
                      (hasActiveBudget ? 'for_execute_service' : 'for_budget')
                    }
                  />
                </Grid>

                {reservationType === 'for_budget' && (
                  <Grid item xs={12}>
                    <Select
                      name="tradesman_id"
                      label="Prestador"
                      required
                      options={tradesmanOptions}
                      getValue={option => option.value}
                      getLabel={option => option.label}
                      getKey={option => option.id}
                      menuProps={{}}
                      customErrorMessage={errors.tradesman_id?.message}
                      defaultValue={editData?.tradesman}
                    />
                  </Grid>
                )}

                {reservationType === 'for_execute_service' && (
                  <Grid item xs={12}>
                    <Select
                      name="budget_id"
                      label="Orçamento"
                      required
                      options={budgetOptions}
                      getValue={option => option.value}
                      getLabel={option => option.label}
                      getKey={option => option.id}
                      menuProps={{}}
                      customErrorMessage={errors.budget_id?.message}
                      defaultValue={editData?.budget}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Radio
                    name="type"
                    label="Tipo de registro"
                    required
                    labelRadio1="Retirada"
                    labelRadio2="Devolução"
                    valueRadio1="retirada"
                    valueRadio2="devolucao"
                    radioGroupClasses="tipoRegistroGroup"
                    radioClasses="tipoRegistroRadio"
                    defaultValue={editData?.type}
                  />
                </Grid>

                <Grid item xs={12}>
                  <DateTimePicker
                    name="date_key_action"
                    label="Data e hora"
                    required
                    referaDatePickerProps={{
                      disableFuture: false,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <PreviewComponent
                    images={attachments}
                    {...previewState}
                    setPreviewState={setPreviewState}
                  />
                  <Typography className={styles.attachmentLabel}>Anexo</Typography>
                  <Attachments
                    label="Anexo"
                    content="images"
                    files={attachments}
                    multiple={false}
                    accept={{
                      'image/jpeg': ['.jpeg', '.jpg'],
                      'image/png': ['.png'],
                      'image/bmp': ['.bmp'],
                    }}
                    onDrop={handleAddAttachment}
                    onRemoveItem={handleRemoveAttachment}
                    onItemClick={handlePictureItemClick}
                  />
                </Grid>
              </Grid>

              {isEditMode && (
                <Grid className={styles.deleteButtonContainer}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={async () => {
                      const confirmed = await isConfirmed()
                      if (confirmed) {
                        handleDelete()
                      }
                    }}
                    disabled={isLoading}
                    className={styles.deleteButton}
                  >
                    Excluir registro
                  </Button>
                </Grid>
              )}
            </form>
          </FormProvider>
        </Modal.Content>

        <Modal.Actions style={{ justifyContent: 'flex-end' }}>
          <Modal.ButtonRed onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Modal.ButtonRed>
          <Modal.ButtonFullBlue onClick={handleSubmit(onSubmit)} disabled={isLoading}>
            Salvar
          </Modal.ButtonFullBlue>
        </Modal.Actions>
      </Modal.Root>

      <ConfirmRemoveDialog
        message="Você deseja deletar este item?"
        description="Tem certeza que deseja excluir este registro de chave? Esta ação não pode ser desfeita."
        type="remove"
      />
    </>
  )
}

export default KeyRegistryModal
